import axios from 'axios';
import { EmployeeConfig, GitLabCommitter } from '../types/EmployeeConfig.ts';

export class GitLabService {
  private readonly baseURL = 'https://git.uniqconsulting.ch/api/v4';
  private readonly projectId = '276'; // numeric project ID for internal/uniqwarehouse
  private readonly token = import.meta.env.VITE_GITLAB_TOKEN || '';

  private get headers() {
    return {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json',
    };
  }

  async fetchEmployeeConfig(): Promise<EmployeeConfig[]> {
    try {
      // Test basic API access with the token
      console.log('Testing GitLab API access...');
      console.log('Base URL:', this.baseURL);
      console.log('Project ID:', this.projectId);
      console.log('Token (first 20 chars):', this.token.substring(0, 20) + '...');
      
      // Try to get current user info to verify token
      try {
        const userResponse = await axios.get(`${this.baseURL}/user`, {
          headers: this.headers,
        });
        console.log('Token valid for user:', userResponse.data.name);
      } catch (userError) {
        console.error('Token validation failed:', userError);
      }
      
      // Try to access project info
      const projectUrl = `${this.baseURL}/projects/${encodeURIComponent(this.projectId)}`;
      console.log('Testing project access:', projectUrl);
      
      try {
        const projectResponse = await axios.get(projectUrl, {
          headers: this.headers,
        });
        console.log('Project found:', projectResponse.data.name);
        console.log('Project ID:', projectResponse.data.id);
        console.log('Default branch:', projectResponse.data.default_branch);
        console.log('Permissions:', projectResponse.data.permissions);
      } catch (projectError) {
        console.error('Cannot access project:', projectError);
        // Don't return mock data - let the CSV fetch attempt continue
      }
      
      // Try to fetch the CSV file
      const csvUrl = `${this.baseURL}/projects/${encodeURIComponent(this.projectId)}/repository/files/external_employee_config.csv/raw`;
      console.log('Fetching CSV from:', csvUrl);
      
      const response = await axios.get(csvUrl, {
        headers: this.headers,
        params: { ref: 'master' },
      });
      
      console.log('✅ CSV loaded successfully, length:', response.data.length);
      return this.parseCSV(response.data);
    } catch (error) {
      console.error('Error fetching employee config:', error);
      
      // Enhanced error logging
      if (axios.isAxiosError(error)) {
        console.error('Status:', error.response?.status);
        console.error('Status Text:', error.response?.statusText);
        console.error('Response Data:', error.response?.data);
      }
      
      // If file doesn't exist, throw error instead of returning mock data
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new Error('CSV file not found in repository. Please ensure external_employee_config.csv exists in the master branch.');
      }
      
      throw new Error('Failed to fetch employee configuration from GitLab');
    }
  }

  async saveEmployeeConfig(employees: EmployeeConfig[], committer: GitLabCommitter): Promise<void> {
    try {
      // Generate CSV content
      const csvContent = this.generateCSV(employees);
      
      // Create a new branch and merge request (master branch is protected)
      const branchName = `employee-config-update-${Date.now()}`;
      console.log(`Creating branch: ${branchName}`);
      
      await this.createBranch(branchName);
      await this.updateFile(branchName, csvContent, committer);
      await this.createMergeRequest(branchName, committer);
      
      console.log('✅ Branch and merge request created successfully');
    } catch (error) {
      console.error('Error saving employee config:', error);
      throw new Error('Failed to save employee configuration to GitLab');
    }
  }

  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    
    return result;
  }

  private parseCSV(csvContent: string): EmployeeConfig[] {
    console.log('Parsing CSV content:', csvContent.substring(0, 200) + '...');
    
    const lines = csvContent.trim().split('\n');
    console.log(`Found ${lines.length} lines in CSV`);
    
    if (lines.length < 2) {
      throw new Error('CSV file is empty or has no data rows');
    }
    
    const headers = this.parseCsvLine(lines[0]);
    console.log('CSV headers:', headers);
    
    const employees = lines.slice(1).map((line, index) => {
      const values = this.parseCsvLine(line);
      const isActiveValue = values[3]?.trim().toLowerCase();
      
      // Generate unique ID based on name if not provided
      const employeeName = values[0]?.trim() || '';
      const uniqueId = employeeName.toLowerCase().replace(/\s+/g, '-') + '-' + (index + 1);
      
      const employee = {
        id: uniqueId, // Unique identifier for each employee
        employee_full_name: employeeName,
        employee_team: values[1]?.trim() || '',
        employee_target_billable_hours: parseFloat(values[2]?.trim() || '0'),
        is_active: (isActiveValue === 'true' || isActiveValue === '1') ? 1 : 0,
      };
      
      if (index < 3) { // Log first 3 employees for debugging
        console.log(`Employee ${index + 1}:`, employee);
      }
      
      return employee;
    });
    
    console.log(`✅ Successfully parsed ${employees.length} employees from CSV`);
    return employees;
  }

  private escapeCsvField(field: string): string {
    // If field contains comma, quote, or newline, wrap in quotes and escape quotes
    if (field.includes(',') || field.includes('"') || field.includes('\n') || field.includes('\r')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  }

  private generateCSV(employees: EmployeeConfig[]): string {
    console.log(`Generating CSV for ${employees.length} employees`);
    
    const headers = [
      'employee_full_name',
      'employee_team', 
      'employee_target_billable_hours',
      'is_active',
    ];

    const rows = employees.map((emp, index) => {
      // Format billable hours: preserve decimals if present, otherwise use integer
      const hoursStr = emp.employee_target_billable_hours % 1 === 0 
        ? emp.employee_target_billable_hours.toString() 
        : emp.employee_target_billable_hours.toFixed(2);
      
      const row = [
        this.escapeCsvField(emp.employee_full_name),
        this.escapeCsvField(emp.employee_team),
        hoursStr,
        emp.is_active ? '1' : '0',
      ];
      
      if (index < 3) { // Log first 3 employees for debugging
        console.log(`CSV row ${index + 1}:`, row);
      }
      
      return row;
    });

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    console.log('Generated CSV preview:', csvContent.substring(0, 200) + '...');
    
    return csvContent;
  }

  private async createBranch(branchName: string): Promise<void> {
    try {
      console.log(`Creating branch: ${branchName}`);
      await axios.post(
        `${this.baseURL}/projects/${encodeURIComponent(this.projectId)}/repository/branches`,
        {
          branch: branchName,
          ref: 'master',
        },
        { headers: this.headers }
      );
      console.log(`✅ Branch created successfully: ${branchName}`);
    } catch (error) {
      console.error(`❌ Failed to create branch: ${branchName}`, error);
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 403) {
          throw new Error(
            `Keine Berechtigung zum Erstellen von Branches. 
            
Lösungsvorschläge:
1. GitLab Access Token benötigt höhere Berechtigungen
2. Ändern Sie Ihre Rolle in der Gruppe von 'Guest' zu 'Developer' oder 'Maintainer'  
3. Kontaktieren Sie den GitLab-Administrator für erweiterte Berechtigungen

Aktueller Token hat nur Leserechte, benötigt aber Schreibrechte für Branch-Erstellung.`
          );
        } else if (error.response?.status === 401) {
          throw new Error('GitLab Access Token ist ungültig oder abgelaufen. Bitte erstellen Sie einen neuen Token.');
        }
      }
      
      throw error;
    }
  }

  private async updateFile(branchName: string, content: string, committer: GitLabCommitter): Promise<void> {
    const filePath = 'external_employee_config.csv';
    
    await axios.put(
      `${this.baseURL}/projects/${encodeURIComponent(this.projectId)}/repository/files/${encodeURIComponent(filePath)}`,
      {
        branch: branchName,
        content: btoa(unescape(encodeURIComponent(content))), // Base64 encode
        encoding: 'base64', // Tell GitLab the content is base64-encoded
        commit_message: `Update employee configuration - ${new Date().toISOString()}`,
        author_email: committer.email,
        author_name: committer.author,
      },
      { headers: this.headers }
    );
  }

  private async getUserId(username: string): Promise<number | undefined> {
    try {
      const response = await axios.get(`${this.baseURL}/users`, {
        params: { username },
        headers: this.headers,
      });
      return response.data[0]?.id;
    } catch {
      console.warn(`Could not find GitLab user ID for @${username}`);
      return undefined;
    }
  }

  async downloadHoursOverview(): Promise<void> {
    const fileName = 'hours_overview.csv';
    const url = `${this.baseURL}/projects/${encodeURIComponent(this.projectId)}/repository/files/${encodeURIComponent(fileName)}/raw`;
    
    const response = await axios.get(url, {
      headers: this.headers,
      params: { ref: 'master' },
      responseType: 'blob',
    });

    // Trigger browser download
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    link.href = URL.createObjectURL(blob);
    link.download = `stunden_uebersicht_${today}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private async createMergeRequest(branchName: string, committer: GitLabCommitter): Promise<void> {
    const [andyId, katjaId] = await Promise.all([
      this.getUserId('andy.bui'),
      this.getUserId('katja.wiesmueller'),
    ]);

    const body: Record<string, unknown> = {
      source_branch: branchName,
      target_branch: 'master',
      title: `Employee Configuration Update - ${new Date().toLocaleDateString()}`,
      description: `Employee configuration updated by ${committer.author} (${committer.email})\n\nChanges made through the UniqWarehouse Employee Configuration Web App.`,
      remove_source_branch: true,
    };

    if (andyId) body.assignee_id = andyId;
    if (katjaId) body.reviewer_ids = [katjaId];

    await axios.post(
      `${this.baseURL}/projects/${encodeURIComponent(this.projectId)}/merge_requests`,
      body,
      { headers: this.headers }
    );
  }
}